import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/cliente';
import { useSesion } from '../auth/sesion';
import { Aviso, Campo, Cargando, Etiqueta, Vacio, fecha, pesos } from '../ui/piezas';

/**
 * Lo que se le debe al propietario cada mes de un arriendo.
 *
 * Cuelga del mandato porque es de donde salen las cifras: la comisión no se
 * escribe, se calcula del porcentaje pactado sobre el canon **recibido**. Si el
 * inquilino pagó de menos, la inmobiliaria cobra de menos; eso es lo que dice
 * el contrato y es lo que hace el servidor.
 */

interface Liquidacion {
  id: string;
  mandatoId: string;
  inmuebleCodigo: string;
  propietario: string;
  periodo: string;
  canonRecibido: number;
  comisionCalculada: number;
  descuentos: Record<string, number> | null;
  netoPropietario: number;
  estado: 'BORRADOR' | 'APROBADA' | 'PAGADA';
  aprobadaEn: string | null;
  pagadaEn: string | null;
  notas: string | null;
}

/* Los saltos que la API acepta. Una liquidación pagada es terminal. */
const SIGUIENTES: Record<Liquidacion['estado'], Liquidacion['estado'][]> = {
  BORRADOR: ['APROBADA'],
  APROBADA: ['PAGADA', 'BORRADOR'],
  PAGADA: [],
};

const ROTULO: Record<Liquidacion['estado'], string> = {
  BORRADOR: 'Reabrir',
  APROBADA: 'Aprobar',
  PAGADA: 'Marcar pagada',
};

/** El mes en curso, en el formato que espera un input de tipo mes. */
function mesActual(): string {
  const hoy = new Date();
  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;
}

function mesLargo(iso: string): string {
  const [anio, mes] = iso.split('-');
  const nombres = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
  ];
  const indice = Number(mes) - 1;
  return nombres[indice] ? `${nombres[indice]} de ${anio}` : iso;
}

function numero(valor: string): number | null {
  const limpio = valor.trim();
  if (limpio === '') return null;
  const leido = Number(limpio);
  return Number.isFinite(leido) ? leido : null;
}

export default function Liquidaciones({
  mandatoId,
  inmuebleId,
}: {
  mandatoId: string;
  inmuebleId: string;
}) {
  const { puede } = useSesion();
  const cliente = useQueryClient();
  const [abierto, setAbierto] = useState(false);
  const [periodo, setPeriodo] = useState(mesActual());
  const [canon, setCanon] = useState('');
  const [predial, setPredial] = useState('');
  const [reparaciones, setReparaciones] = useState('');
  const [notas, setNotas] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [exito, setExito] = useState<string | null>(null);

  const listado = useQuery({
    queryKey: ['liquidaciones', mandatoId],
    queryFn: () => api<Liquidacion[]>(`/admin/liquidaciones?mandato=${mandatoId}`),
    enabled: puede('liquidacion:leer'),
  });

  const refrescar = () =>
    void cliente.invalidateQueries({ queryKey: ['liquidaciones', mandatoId] });

  const registrar = useMutation({
    mutationFn: () => {
      const descuentos: Record<string, number> = {};
      const conPredial = numero(predial);
      const conReparaciones = numero(reparaciones);
      if (conPredial !== null) descuentos.predial = conPredial;
      if (conReparaciones !== null) descuentos.reparaciones = conReparaciones;

      return api<Liquidacion>(`/admin/liquidaciones/inmuebles/${inmuebleId}`, {
        metodo: 'POST',
        cuerpo: {
          mandatoId,
          // La API normaliza al día 1; el día que se mande da igual.
          periodo: `${periodo}-01`,
          canonRecibido: numero(canon) ?? 0,
          descuentos: Object.keys(descuentos).length > 0 ? descuentos : null,
          notas: notas.trim() === '' ? null : notas.trim(),
        },
      });
    },
    onSuccess: (creada) => {
      setCanon('');
      setPredial('');
      setReparaciones('');
      setNotas('');
      setAbierto(false);
      setError(null);
      setExito(`${mesLargo(periodo)}: neto al propietario ${pesos(creada.netoPropietario)}.`);
      refrescar();
    },
    onError: (e) => {
      setExito(null);
      setError(e);
    },
  });

  const cambiarEstado = useMutation({
    mutationFn: ({ id, destino }: { id: string; destino: string }) =>
      api<Liquidacion>(`/admin/liquidaciones/${id}/estado/${destino}`, { metodo: 'POST' }),
    onSuccess: () => {
      setError(null);
      setExito(null);
      refrescar();
    },
    onError: (e) => setError(e),
  });

  if (!puede('liquidacion:leer')) return null;

  const liquidaciones = listado.data ?? [];
  const puedeRegistrar = puede('inmueble:editar');
  const puedeAprobar = puede('liquidacion:aprobar');

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--linea)' }}>
      <b style={{ fontSize: 14 }}>Liquidaciones mensuales</b>
      <p style={{ opacity: 0.7, fontSize: 13.5, margin: '4px 0 10px' }}>
        La comisión se calcula sola, del porcentaje del mandato sobre el canon
        recibido. Registrar dos veces el mismo mes corrige las cifras, no duplica.
      </p>

      <Aviso error={error} exito={exito} />

      {listado.isPending ? (
        <Cargando />
      ) : liquidaciones.length === 0 ? (
        <Vacio>Todavía no hay meses registrados.</Vacio>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {liquidaciones.map((l) => (
            <div
              key={l.id}
              style={{
                border: '1px solid var(--linea)',
                borderRadius: 8,
                padding: 10,
                fontSize: 14,
              }}
            >
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <Etiqueta>{l.estado}</Etiqueta>
                <b>{mesLargo(l.periodo.slice(0, 7))}</b>
              </div>
              <div
                style={{
                  marginTop: 6,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: 6,
                  opacity: 0.85,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                <div>Canon recibido: {pesos(l.canonRecibido)}</div>
                <div>Comisión: {pesos(l.comisionCalculada)}</div>
                {l.descuentos &&
                  Object.entries(l.descuentos).map(([concepto, valor]) => (
                    <div key={concepto}>
                      {concepto}: {pesos(valor)}
                    </div>
                  ))}
                <div>
                  <b>Neto al propietario: {pesos(l.netoPropietario)}</b>
                </div>
              </div>
              {l.pagadaEn && (
                <div style={{ marginTop: 4, opacity: 0.7 }}>Pagada el {fecha(l.pagadaEn)}</div>
              )}
              {l.notas && <div style={{ marginTop: 4, opacity: 0.8 }}>{l.notas}</div>}

              {puedeAprobar && SIGUIENTES[l.estado].length > 0 && (
                <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {SIGUIENTES[l.estado].map((destino) => (
                    <button
                      key={destino}
                      className="plano"
                      disabled={cambiarEstado.isPending}
                      onClick={() => cambiarEstado.mutate({ id: l.id, destino })}
                    >
                      {ROTULO[destino]}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {puedeRegistrar && (
        <>
          <div style={{ height: 10 }} />
          <button className="plano" style={{ paddingLeft: 0 }} onClick={() => setAbierto(!abierto)}>
            {abierto ? 'Cancelar' : 'Registrar un mes'}
          </button>
        </>
      )}

      {abierto && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Campo etiqueta="Mes" tipo="month" valor={periodo} alCambiar={setPeriodo} requerido />
            <Campo
              etiqueta="Canon recibido"
              ayuda="Lo que de verdad entró, no lo pactado."
              valor={canon}
              alCambiar={setCanon}
              requerido
            />
            <Campo etiqueta="Predial" ayuda="Descuento, si lo hubo." valor={predial} alCambiar={setPredial} />
            <Campo
              etiqueta="Reparaciones"
              ayuda="Descuento, si lo hubo."
              valor={reparaciones}
              alCambiar={setReparaciones}
            />
          </div>
          <div style={{ height: 12 }} />
          <Campo etiqueta="Notas" valor={notas} alCambiar={setNotas} />
          <div style={{ height: 12 }} />
          <button disabled={canon.trim() === '' || registrar.isPending} onClick={() => registrar.mutate()}>
            {registrar.isPending ? 'Calculando…' : 'Registrar el mes'}
          </button>
        </div>
      )}
    </div>
  );
}
